import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';

export function SettingsPanel({
  settings,
  onUpdateSettings,
  embedded = false,
}) {
  const [isExpanded, setIsExpanded] = useState(true); // Standardmäßig offen

  // Default-Werte (müssen mit StoreyEditor3D übereinstimmen!)
  const DEFAULTS = {
    viewMode: '2d',         // '2d' oder '3d'
    pointRadius: 100,
    wallWidth: 4,
    showLabels: true,
    labelSize: 400,
    showDimensions: true,
    dimensionFontSize: 250,
    showGrid: true,
    gridOpacity: 0.2,
    showSpaceFill: true,
    spaceFillOpacity: 0.15,
    showWallOpenings: true,
    snapThreshold: 500,
    // 3D-Deckenansicht
    showLots: true,
    showCeilingPoints: true,
    showCeilingLines: true,
    showCeilingFill: true,
    showHeightLabels: true,
  };

  const {
    viewMode = DEFAULTS.viewMode,
    pointRadius = DEFAULTS.pointRadius,
    wallWidth = DEFAULTS.wallWidth,
    showLabels = DEFAULTS.showLabels,
    labelSize = DEFAULTS.labelSize,
    showDimensions = DEFAULTS.showDimensions,
    dimensionFontSize = DEFAULTS.dimensionFontSize,
    showGrid = DEFAULTS.showGrid,
    gridOpacity = DEFAULTS.gridOpacity,
    showSpaceFill = DEFAULTS.showSpaceFill,
    spaceFillOpacity = DEFAULTS.spaceFillOpacity,
    showWallOpenings = DEFAULTS.showWallOpenings,
    snapThreshold = DEFAULTS.snapThreshold,
    // 3D-Deckenansicht
    showLots = DEFAULTS.showLots,
    showCeilingPoints = DEFAULTS.showCeilingPoints,
    showCeilingLines = DEFAULTS.showCeilingLines,
    showCeilingFill = DEFAULTS.showCeilingFill,
    showHeightLabels = DEFAULTS.showHeightLabels,
  } = settings || {};

  const handleChange = (key, value) => {
    onUpdateSettings?.({ ...settings, [key]: value });
  };

  const content = (
    <div className="p-3 space-y-4">
      {/* Ansichtsmodus */}
      <Section title="Ansicht">
        <div className="flex gap-2">
          <button
            onClick={() => handleChange('viewMode', '2d')}
            className={`flex-1 px-3 py-2 text-sm rounded ${
              viewMode === '2d'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            2D Grundriss
          </button>
          <button
            onClick={() => handleChange('viewMode', '3d')}
            className={`flex-1 px-3 py-2 text-sm rounded ${
              viewMode === '3d'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            3D Ansicht
          </button>
        </div>
      </Section>

      {/* 3D-Deckenansicht (nur wenn 3D aktiv) */}
      {viewMode === '3d' && (
        <Section title="3D-Deckenansicht">
          <ToggleRow
            label="Lot-Linien (vertikal)"
            checked={showLots}
            onChange={(v) => handleChange('showLots', v)}
          />
          <ToggleRow
            label="Deckenpunkte"
            checked={showCeilingPoints}
            onChange={(v) => handleChange('showCeilingPoints', v)}
          />
          <ToggleRow
            label="Deckenlinien"
            checked={showCeilingLines}
            onChange={(v) => handleChange('showCeilingLines', v)}
          />
          <ToggleRow
            label="Deckenflächen"
            checked={showCeilingFill}
            onChange={(v) => handleChange('showCeilingFill', v)}
          />
          <ToggleRow
            label="Höhenbeschriftung"
            checked={showHeightLabels}
            onChange={(v) => handleChange('showHeightLabels', v)}
          />
        </Section>
      )}

      {/* Anzeige-Optionen */}
      <Section title="Anzeige">
        <ToggleRow
          label="Grid anzeigen"
          checked={showGrid}
          onChange={(v) => handleChange('showGrid', v)}
        />
        {showGrid && (
          <SliderRow
            label="Grid-Deckkraft"
            value={gridOpacity}
            min={0.05}
            max={1}
            step={0.05}
            onChange={(v) => handleChange('gridOpacity', v)}
          />
        )}

        <ToggleRow
          label="Raumflächen füllen"
          checked={showSpaceFill}
          onChange={(v) => handleChange('showSpaceFill', v)}
        />
        {showSpaceFill && (
          <SliderRow
            label="Füll-Deckkraft"
            value={spaceFillOpacity}
            min={0.02}
            max={0.9}
            step={0.02}
            onChange={(v) => handleChange('spaceFillOpacity', v)}
          />
        )}

        <ToggleRow
          label="Öffnungen anzeigen"
          checked={showWallOpenings}
          onChange={(v) => handleChange('showWallOpenings', v)}
        />
      </Section>

      {/* Größen */}
      <Section title="Größen">
        <SliderRow
          label="Punktgröße"
          value={pointRadius}
          min={5}
          max={500}
          step={5}
          onChange={(v) => handleChange('pointRadius', v)}
          unit="mm"
        />

        <SliderRow
          label="Linienbreite"
          value={wallWidth}
          min={1}
          max={12}
          step={0.5}
          onChange={(v) => handleChange('wallWidth', v)}
          unit="px"
        />

        <SliderRow
          label="Snap-Toleranz"
          value={snapThreshold}
          min={5}
          max={2000}
          step={5}
          onChange={(v) => handleChange('snapThreshold', v)}
          unit="mm"
        />
      </Section>

      {/* Beschriftungen */}
      <Section title="Beschriftungen">
        <ToggleRow
          label="Raumnamen anzeigen"
          checked={showLabels}
          onChange={(v) => handleChange('showLabels', v)}
        />
        {showLabels && (
          <SliderRow
            label="Schriftgröße"
            value={labelSize}
            min={100}
            max={1000}
            step={50}
            onChange={(v) => handleChange('labelSize', v)}
            unit="mm"
          />
        )}

        <ToggleRow
          label="Maße anzeigen"
          checked={showDimensions}
          onChange={(v) => handleChange('showDimensions', v)}
        />
        {showDimensions && (
          <SliderRow
            label="Maß-Schriftgröße"
            value={dimensionFontSize}
            min={50}
            max={600}
            step={25}
            onChange={(v) => handleChange('dimensionFontSize', v)}
            unit="mm"
          />
        )}
      </Section>

      {/* Reset */}
      <button
        onClick={() => onUpdateSettings?.(DEFAULTS)}
        className="w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
      >
        Auf Standard zurücksetzen
      </button>
    </div>
  );

  // Im embedded-Modus direkt den Inhalt zurückgeben
  if (embedded) {
    return content;
  }

  // Standalone mit eigenem Container - nutze content Variable
  return (
    <div
      className="absolute left-4 bottom-14 w-64 bg-white rounded-lg shadow-lg z-10 max-h-[60vh] overflow-hidden flex flex-col"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full p-3 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Settings size={18} className="text-gray-600" />
          <span className="font-semibold text-gray-800">Einstellungen</span>
        </div>
        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>

      {isExpanded && (
        <div className="flex-1 overflow-y-auto">
          {content}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 uppercase mb-2">{title}</div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-700">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          checked ? 'bg-blue-500' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, unit = '' }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-600">{label}</span>
        <span className="text-xs font-mono text-gray-500">
          {typeof value === 'number' ? value.toFixed(step < 1 ? 1 : 0) : value} {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
      />
    </div>
  );
}
